import { action, computed, observable, makeObservable } from 'mobx';
import { loadable } from '@fulcrumgt/mobx-store-utils';
import { RootStore } from 'store/root.store';
import ImmutableTimer from 'api/immutables/ImmutableTimer';
import { Client, Matter, TargetTimer } from '../../api/types/types';
import DialogRootStore from 'store/dialog.root.store';

export default class TransferTimerDialogStore extends DialogRootStore<number[], void> {
  @observable chunkIds: number[] = [];
  @observable sourceTimerId: number;
  @observable targetTimerId?: number;
  @observable saving: boolean = false;
  @observable clientFilter: Client | null = null;
  @observable matterFilter: Matter | null = null;
  @observable clientFilteredTimers: ImmutableTimer[] = [];
  @observable matterFilteredTimers: ImmutableTimer[] = [];
  @observable timers: ImmutableTimer[] = [];
  @observable searchText = '';
  @observable expanded: boolean = false;
  @observable isFromTransferDialog: boolean = false;

  constructor(root: RootStore) {
    super(root);
    makeObservable(this);
  }

  @computed
  get filteredTimers(): ImmutableTimer[] {
    const { timers, sortTimers } = this.rootStore.timerStore;
    let baseFilteredTimers = sortTimers(timers);
    if (this.clientFilter) {
      baseFilteredTimers = this.clientFilteredTimers;
    }
    if (this.matterFilter) {
      baseFilteredTimers = this.matterFilteredTimers;
    }
    if (this.searchText) {
      baseFilteredTimers = baseFilteredTimers.filter((timer: ImmutableTimer) =>
        timer.name.toLowerCase().includes(this.searchText.toLowerCase())
      );
    }
    return baseFilteredTimers;
  }

  @loadable()
  @action.bound
  async setClientFilter(client: Client | null) {
    this.clientFilter = client;
    this.clientFilteredTimers = client ? await this.rootStore.api.Timer.filterTimersByClient(client.id) : [];
    this.setMatterFilter(null);
  }

  @action.bound
  setSearchText(text: string) {
    this.searchText = text;
  }

  @loadable()
  @action.bound
  async setMatterFilter(matter: Matter | null) {
    if (!matter) {
      this.matterFilteredTimers = [];
      this.matterFilter = null;
      return;
    }

    const client: Client = {
      id: matter.clientId,
      name: matter.clientName,
      number: matter.clientNumber
    };
    await this.setClientFilter(client);
    this.matterFilter = matter;
    this.matterFilteredTimers = await this.rootStore.api.Timer.filterTimersByMatter(matter.id);
  }

  @loadable()
  @action.bound
  async onOpen(chunkIds: number[]) {
    this.isFromTransferDialog = true;
    this.chunkIds = chunkIds;
    this.sourceTimerId = this.rootStore.timerStore.selectedTimer.id!;
    this.targetTimerId = undefined;
    this.searchText = '';
    this.clientFilter = null;
    this.matterFilter = null;
    this.expanded = false;
  }

  @loadable()
  @action.bound
  closeTransferDialog() {
    this.resolveAndClose();
    this.saving = false;
    this.isFromTransferDialog = false;
    if (this.rootStore.newTimerDialogStore.isOpen) {
      this.rootStore.newTimerDialogStore.resolveAndClose([]);
    }
  }

  @loadable()
  @action.bound
  async transferChunks() {
    this.saving = true;
    if (!this.targetTimerId) {
      const createdTimers = await this.rootStore.newTimerDialogStore.saveNew();
      if (!createdTimers || createdTimers.length === 0 || !createdTimers[0].id) {
        this.saving = false;
        return;
      }
      this.targetTimerId = createdTimers[0].id;
    }
    const targetTimer: TargetTimer = {
      id: this.targetTimerId,
      timeKeeperId: this.rootStore.appStore.currentTimekeeper.timeKeeperId,
    };
    const apiResult = await this.rootStore.api.Timer.transferChunks(this.sourceTimerId, this.chunkIds, targetTimer);
    if (apiResult.status.failed) {
      this.rootStore.snackbarStore.triggerSnackbar(apiResult.status.message);
      this.closeTransferDialog();
      return;
    }
    const updatedTimers = [
      await this.rootStore.api.Timer.get(this.sourceTimerId!),
      await this.rootStore.api.Timer.get(this.targetTimerId!)
    ];
    this.rootStore.timerStore.changeSelectedTimer(this.targetTimerId!);
    this.rootStore.timerStore.recieveTimers(updatedTimers);
    this.rootStore.timerStore.clearSelections();
    this.rootStore.snackbarStore.triggerSnackbar(
      `dialog.transfer_chunk.action.transfer.success`, { ns: 'timers' }
    );
    this.closeTransferDialog();
  }

  @action.bound
  setTargetTimerId(timerId?: number) {
    this.targetTimerId = timerId;
  }

  @action.bound
  setExpanded() {
    this.expanded = !this.expanded;
  }
}